/**
 * Syncs workspace store to config default tenant/project on fresh/incognito sessions.
 * Runs for all authenticated users but only sets workspace when it is currently empty
 * (tenantId is null), so it never overrides an explicit selection made by a tech admin.
 */

import React, { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listTenants } from '../../api/tenants';
import { listProjects } from '../../api/projects';
import { useWorkspaceStore } from '../../stores/workspace';

export interface WorkspaceDefaultSyncProps {
  defaultTenantId: string | undefined;
  defaultProjectId: string | undefined;
}

export function WorkspaceDefaultSync({ defaultTenantId, defaultProjectId }: WorkspaceDefaultSyncProps) {
  const { setTenants, setProjects, setTenant, setProject } = useWorkspaceStore();
  const lastSyncedRef = useRef<{ tenantId: string; projectId: string } | null>(null);

  const { data: tenants } = useQuery({
    queryKey: ['tenants'],
    queryFn: listTenants,
    enabled: !!defaultTenantId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: projects } = useQuery({
    queryKey: ['projects', defaultTenantId],
    queryFn: () => listProjects(defaultTenantId),
    enabled: !!defaultTenantId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!tenants?.length) return;
    setTenants(tenants);
  }, [tenants, setTenants]);

  useEffect(() => {
    if (!defaultTenantId || !defaultProjectId || !tenants?.length || !projects?.length) return;

    const tenant = tenants.find((t) => t.id === defaultTenantId);
    const project = projects.find((p) => p.id === defaultProjectId);
    if (!tenant || !project) return;

    const store = useWorkspaceStore.getState();
    const storeMatches = store.tenantId === defaultTenantId && store.projectId === defaultProjectId;

    // Already pointing at the right project — nothing to do
    if (storeMatches) {
      lastSyncedRef.current = { tenantId: defaultTenantId, projectId: defaultProjectId };
      return;
    }

    // Only auto-set when the workspace is empty (fresh/incognito session).
    // If the store already has a different project (tech admin changed it), leave it alone.
    const workspaceIsEmpty = store.tenantId === null && store.projectId === null;
    if (!workspaceIsEmpty) return;

    setTenants(tenants);
    setProjects(projects);
    setTenant(tenant);
    setProject(project);
    lastSyncedRef.current = { tenantId: defaultTenantId, projectId: defaultProjectId };
  }, [defaultTenantId, defaultProjectId, tenants, projects, setTenant, setProject, setTenants, setProjects]);

  return null;
}
